'use client';

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Review } from '@/lib/Apis/models';
import { UsersApi } from '@/lib/Apis/apis/users-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { StarRating } from './StarRating';
import { MessageCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ReviewCardProps {
  review: Review;
  showProductInfo?: boolean;
}

const AVATAR_COLORS = [
  'bg-hsecondary/8',
];

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export function ReviewCard({ review, showProductInfo = false }: ReviewCardProps) {
  const reviewDate = new Date(review.createdAt);
  const replyDate = review.replyDate ? new Date(review.replyDate) : null;

  const rawReview = review as any;
  const userId: string | undefined = rawReview.userId;

  const { data: userData } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => new UsersApi(AXIOS_CONFIG).getSingleUser(userId!),
    enabled: !!userId,
    select: (res) => res.data,
  });

  const fetchedName = userData
    ? [userData.firstname, userData.lastname].filter(Boolean).join(' ')
    : undefined;

  const displayName: string =
    fetchedName ||
    rawReview.customerName ||
    rawReview.userName ||
    rawReview.user?.name ||
    'Customer';

  const initials = getInitials(displayName);
  const avatarColor = getAvatarColor(review.id || review._id || displayName);

  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 ${avatarColor} rounded-full flex items-center justify-center shrink-0`}>
            <span className="text-hsecondary text-[13px]">{initials}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-[14px]">{displayName}</p>
            <p className="text-xs text-hsecondary opacity-50">
              {formatDistanceToNow(reviewDate, { addSuffix: true })}
            </p>
          </div>
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>

      <p className="text-gray-700 text-[13px] leading-relaxed">{review.review}</p>

      {review.reply && (
        <div className="mt-4 pl-4 border-l-2 border-hsecondary bg-gray-50 p-3 rounded">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="size-4 text-hsecondary" />
            <p className="text-xs font-medium text-gray-900">Store Response</p>
            {replyDate && (
              <p className="text-xs text-gray-500">
                {formatDistanceToNow(replyDate, { addSuffix: true })}
              </p>
            )}
          </div>
          <p className="text-sm text-gray-700">{review.reply}</p>
        </div>
      )}
    </div>
  );
}
